/**
 * PublicCandidateForm — what an applicant sees at /candidate-intake/{token}.
 *
 * Unauthenticated; the unguessable token is the only gate. Mirrors the
 * internal employee record (contact, personal, business, certificates,
 * visas & permits) minus anything commercial — rates, department and
 * positions are filled in by a manager after the card lands.
 *
 * Bilingual (EN / PL) with a toggle in the header; the dictionary lives
 * here rather than in the app's i18n bundle so the public page stays
 * self-contained and loads without the authenticated app.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { compressImage, COMPRESSION_DEFAULTS } from '../lib/imageCompress';
import { DateOverlay, dateTextHiddenCls } from './ui/input';
import { IntakeCustomBlock, missingRequired } from './IntakeCustomBlock';
import { LinkContext, findLinks, blockTexts } from './shared/LinkText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANG_KEY = 'expertfix-intake-lang';
const draftKey = (token) => `expertfix-intake-draft-${token}`;

// Texts live in the i18n catalogue under intakeForm.* (editable in Settings > Translations).

const CERT_GROUPS = [
  { key: 'electrical', field: 'electrical_certs', label: 'electrical' },
  { key: 'scissor_lift', field: 'scissor_lift_certs', label: 'scissor' },
  { key: 'boom_lift', field: 'boom_lift_certs', label: 'boom' },
  { key: 'osha', field: 'osha_certs', label: 'osha' },
  { key: 'health_safety', field: 'health_safety_certs', label: 'hs' },
];

const inputCls = 'w-full h-11 sm:h-10 rounded-md border border-slate-300 px-3 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-slate-400';

const Section = ({ title, hint, children, testId }) => (
  <section className="border-t border-slate-200 pt-5 mt-5" data-testid={testId}>
    <h2 className="text-base md:text-lg font-semibold text-slate-800">{title}</h2>
    {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    <div className="mt-3 space-y-3">{children}</div>
  </section>
);

/** One row of the form: one or two related fields side by side. A lone
 *  field keeps its half width and leaves the space beside it empty. */
const Row = ({ children, full = false }) => (
  <div className={`grid grid-cols-1 gap-3 ${full ? '' : 'sm:grid-cols-2'}`}>{children}</div>
);

const Field = ({ label, required = false, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </span>
    {children}
  </label>
);

/**
 * `Text`, `Select` and `LangToggle` MUST stay at module scope.
 *
 * They used to be declared inside `PublicCandidateForm`, which created a
 * brand-new component type on every render. React then unmounted the old
 * `<input>` and mounted a fresh one after each keystroke, so the field lost
 * focus and applicants had to click back in for every single character.
 * They read what they need from a context instead of closing over state.
 */
const IntakeCtx = React.createContext(null);

const Text = ({ field, testId, type = 'text', required = false }) => {
  const { form, set } = useContext(IntakeCtx);
  const input = (
    <input
      type={type}
      required={required}
      className={`${inputCls}${type === 'date' ? ` peer ${dateTextHiddenCls}` : ''}`}
      value={form[field] || ''}
      onChange={(e) => set(field, e.target.value)}
      data-testid={testId}
    />
  );
  // Dates are painted day-first over the native field — see ui/input.jsx.
  if (type !== 'date') return input;
  return (
    <span className="relative block">
      {input}
      <DateOverlay value={form[field] || ''} />
    </span>
  );
};

const Select = ({ field, options, testId }) => {
  const { form, set, t, vl } = useContext(IntakeCtx);
  return (
    <select className={inputCls} value={form[field] || ''} onChange={(e) => set(field, e.target.value)} data-testid={testId}>
      <option value="">{t('none')}</option>
      {(options || []).map((x) => <option key={x} value={x}>{vl(x)}</option>)}
    </select>
  );
};

const LangToggle = () => {
  const { lang, pickLang } = useContext(IntakeCtx);
  return (
    <div className="inline-flex rounded-full border border-slate-300 overflow-hidden text-xs" data-testid="intake-lang-toggle">
      {['en', 'pl'].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => pickLang(code)}
          className={`px-3 py-1.5 font-medium ${lang === code ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
          data-testid={`intake-lang-${code}`}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export default function PublicCandidateForm() {
  const { token } = useParams();
  // `?i=` is present when the link arrived by email — it lets the office
  // see that the invite was opened and, later, completed.
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('i');

  // An invite sent in Polish carries `?lang=pl`, so the form opens in
  // the same language as the email that brought them here.
  const [lang, setLang] = useState(() => {
    const asked = (searchParams.get('lang') || '').toLowerCase();
    if (asked === 'pl' || asked === 'en') return asked;
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === 'pl' || saved === 'en') return saved;
    return (navigator.language || '').toLowerCase().startsWith('pl') ? 'pl' : 'en';
  });
  useTranslation(); // re-render when translation overrides arrive
  const t = useMemo(() => {
    const idx = lang === 'pl' ? 1 : 0;
    const ft = i18n.getFixedT(idx ? "pl" : "en");
    return (key, vars = {}) => ft(`intakeForm.${key}`, { defaultValue: key, ...vars });
  }, [lang]);

  const [meta, setMeta] = useState(null);
  const [opened, setOpened] = useState({});
  const linkCtx = useMemo(() => ({
    fileUrl: (fid) => `${API}/public/candidate-intake/${token}/template-files/${fid}`,
    onOpen: (href) => setOpened((o) => (o[href] ? o : { ...o, [href]: new Date().toISOString() })),
  }), [token]);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  // Saved progress — phones get interrupted, and this form is long.
  // Attachments can't be kept (browsers won't let us re-create a File),
  // everything typed is.
  const [form, setForm] = useState(() => {
    try {
      const raw = localStorage.getItem(draftKey(token));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') return { emergency_contact: {}, ...parsed };
      }
    } catch { /* corrupted draft — ignore */ }
    return { emergency_contact: {} };
  });
  const [restored, setRestored] = useState(() => !!localStorage.getItem(draftKey(token)));
  const [files, setFiles] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const [done, setDone] = useState(false);

  const pickLang = (next) => { setLang(next); localStorage.setItem(LANG_KEY, next); };

  // Keep the draft current, but only once there is something worth keeping.
  useEffect(() => {
    const meaningful = Object.entries(form).some(([k, v]) => (
      k !== 'emergency_contact' && v !== '' && v !== null && v !== undefined
        && !(Array.isArray(v) && v.length === 0)
    )) || Object.values(form.emergency_contact || {}).some(Boolean);
    if (!meaningful) return;
    try { localStorage.setItem(draftKey(token), JSON.stringify(form)); } catch { /* full */ }
  }, [form, token]);

  const clearDraft = () => {
    localStorage.removeItem(draftKey(token));
    setForm({ emergency_contact: {} });
    setFiles([]);
    setFileTypes([]);
    setRestored(false);
  };

  useEffect(() => {
    axios.get(`${API}/public/candidate-intake/${token}`, {
      params: inviteToken ? { invite: inviteToken } : {},
    })
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e.response?.data?.detail || null));
    axios.get(`${API}/company/profile/public`)
      .then((r) => setCompany(r.data))
      .catch(() => setCompany(null));
  }, [token, inviteToken]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEc = (k, v) => setForm((f) => ({ ...f, emergency_contact: { ...(f.emergency_contact || {}), [k]: v } }));

  const certChecked = (field, type) => (form[field] || []).some((c) => c.type === type);
  const certExpiry = (field, type) => (form[field] || []).find((c) => c.type === type)?.expiry_date || '';
  const toggleCert = (field, type, on) => setForm((f) => {
    const list = f[field] || [];
    return {
      ...f,
      [field]: on ? [...list, { type, expiry_date: null }] : list.filter((c) => c.type !== type),
    };
  });
  const setCertExpiry = (field, type, date) => setForm((f) => ({
    ...f,
    [field]: (f[field] || []).map((c) => (c.type === type ? { ...c, expiry_date: date || null } : c)),
  }));

  const pickFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const max = meta?.max_files || 6;
    setFiles((prev) => [...prev, ...chosen].slice(0, max));
    setFileTypes((prev) => [...prev, ...chosen.map(() => '')].slice(0, max));
    e.target.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim()) { toast.error(t('errFirst')); return; }
    if (!form.last_name?.trim()) { toast.error(t('errLast')); return; }
    if (!form.phone?.trim()) { toast.error(t('errPhone')); return; }
    if (!form.email?.trim()) { toast.error(t('errEmail')); return; }
    const missing = missingRequired(meta?.blocks, form.custom_answers, lang);
    if (missing) { toast.error(`${t('errRequired')}: ${missing}`); return; }
    const unopened = findLinks(blockTexts(meta?.blocks, lang)).find((l) => l.required && !opened[l.href]);
    if (unopened) { toast.error(`${t('errOpenLink')}: ${unopened.label}`); return; }
    setSubmitting(true);
    try {
      const name = [form.first_name, form.middle_name, form.last_name]
        .map((p) => (p || '').trim()).filter(Boolean).join(' ');
      const { data } = await axios.post(`${API}/public/candidate-intake/${token}/submit`, {
        data: { ...form, name, opened_links: Object.entries(opened).map(([href, at]) => ({ href, at })) },
        invite_token: inviteToken || null,
        language: lang,
      });

      for (let i = 0; i < files.length; i++) {
        setProgress(t('uploading', { i: i + 1, n: files.length }));
        // Shrink photos on the device — passport snaps off a phone are
        // routinely 5 MB+ and the applicant may be on mobile data.
        const prepared = await compressImage(files[i], COMPRESSION_DEFAULTS);
        const fd = new FormData();
        fd.append('file', prepared);
        fd.append('kind', prepared.type?.startsWith('image/') ? 'photo' : 'document');
        // Same document types the office uses on employee attachments,
        // so the file lands correctly filed.
        if (fileTypes[i]) fd.append('category', fileTypes[i]);
        try {
          await axios.post(
            `${API}/public/candidate-intake/${token}/files/${data.upload_token}`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
        } catch (err) {
          toast.error(t('errUpload', {
            name: files[i].name,
            why: err.response?.data?.detail || t('errUploadWhy'),
          }));
        }
      }
      localStorage.removeItem(draftKey(token));
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errSubmit'));
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  const companyName = meta?.company_name || company?.name;
  const logoFile = company?.header_logo_file || company?.logo_file;
  const logoUrl = logoFile
    ? `${process.env.REACT_APP_BACKEND_URL}/api/company/${company?.header_logo_file ? 'header-logo' : 'logo'}/${logoFile}`
    : null;

  if (error !== null) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md text-center bg-white rounded-lg shadow p-6" data-testid="intake-error">
        <h1 className="text-lg font-semibold text-red-600">{t('unavailable')}</h1>
        <p className="text-sm text-slate-600 mt-2">{error || t('invalid')}</p>
      </div>
    </div>
  );
  if (!meta) return <div className="p-12 text-center text-slate-500">{t('loading')}</div>;
  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md text-center bg-white rounded-lg shadow p-8" data-testid="intake-thanks">
        {logoUrl && <img src={logoUrl} alt="" className="h-10 w-auto object-contain mx-auto mb-4" />}
        <div className="text-5xl mb-3">✓</div>
        <h1 className="text-xl font-semibold text-green-700">{t('thanks')}</h1>
        <p className="text-sm text-slate-600 mt-2">
          {t('thanksBody', { to: companyName ? t('thanksTo', { company: companyName.replace(/\.$/, '') }) : '' })}
        </p>
      </div>
    </div>
  );

  const o = meta.options || {};
  const certTypes = o.certificate_types || {};
  // Values stay English (that's what the office stores and reports on);
  // only the caption a Polish reader sees changes.
  const valueLabels = (meta.value_labels || {})[lang] || {};
  const vl = (value) => valueLabels[value] || value;
  const yesNo = [
    { v: '', l: t('none') },
    { v: 'yes', l: t('yes') },
    { v: 'no', l: t('no') },
    { v: 'pending', l: t('pending') },
  ];
  const ctx = { form, set, t, vl, lang, pickLang };
  const builtinSections = {
    personal: (
        <Section title={t('secPersonal')} testId="intake-section-personal">
          <Row>
            <Field label={t('birthDate')}><Text field="birth_date" type="date" testId="intake-birth-date" /></Field>
            <Field label={t('birthPlace')}><Text field="birth_place" /></Field>
          </Row>
          <Row>
            <Field label={t('personalId')}><Text field="personal_id_number" /></Field>
          </Row>
          <Row>
            <Field label={t('idCard')}><Text field="id_card_number" /></Field>
            <Field label={t('idCardExpiry')}><Text field="id_card_expiry_date" type="date" /></Field>
          </Row>
          <Row>
            <Field label={t('drivingNo')}><Text field="driving_licence_number" /></Field>
            <Field label={t('drivingExpiry')}><Text field="driving_licence_expiry" type="date" /></Field>
          </Row>
          <Row>
            <Field label={t('passportNo')}><Text field="passport_number" testId="intake-passport-number" /></Field>
            <Field label={t('passportIssued')}><Text field="passport_issue_date" type="date" /></Field>
          </Row>
          {/* Expiry sits under the issue date (right column) so the two
              passport dates read as a pair. */}
          <Row>
            <div className="hidden sm:block" aria-hidden="true" />
            <Field label={t('passportExpiry')}><Text field="passport_expiry_date" type="date" /></Field>
          </Row>
          <Row>
            <Field label={t('criminal')}>
              <select className={inputCls} value={form.criminal_record || ''} onChange={(e) => set('criminal_record', e.target.value)}>
                <option value="">{t('none')}</option>
                <option value="clean">{t('crClean')}</option>
                <option value="pending">{t('crPending')}</option>
                <option value="issues">{t('crIssues')}</option>
              </select>
            </Field>
            <Field label={t('crIssued')}><Text field="cr_cert_issue_date" type="date" /></Field>
          </Row>
        </Section>
    ),
    emergency: (
        <Section title={t('secEmergency')} testId="intake-section-emergency">
          <Row>
            <Field label={t('ecName')}>
              <input className={inputCls} value={form.emergency_contact?.name || ''} onChange={(e) => setEc('name', e.target.value)} />
            </Field>
            <Field label={t('ecSurname')}>
              <input className={inputCls} value={form.emergency_contact?.surname || ''} onChange={(e) => setEc('surname', e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label={t('phone')}>
              <input className={inputCls} value={form.emergency_contact?.phone || ''} onChange={(e) => setEc('phone', e.target.value)} />
            </Field>
            <Field label={t('email')}>
              <input className={inputCls} value={form.emergency_contact?.email || ''} onChange={(e) => setEc('email', e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label={t('ecRelationship')}>
              <select
                className={inputCls}
                value={form.emergency_contact?.relationship || ''}
                onChange={(e) => setEc('relationship', e.target.value)}
              >
                <option value="">{t('none')}</option>
                {(o.relationships || []).map((r) => <option key={r} value={r}>{vl(r)}</option>)}
              </select>
            </Field>
          </Row>
        </Section>
    ),
    business: (
        <Section title={t('secBusiness')} hint={t('businessHint')} testId="intake-section-business">
          <Row>
            <Field label={t('businessName')}><Text field="business_name" testId="intake-business-name" /></Field>
            <Field label={t('taxId')}><Text field="tax_id_number" /></Field>
          </Row>
          <Row full>
            <Field label={t('businessAddress')}><Text field="business_address" /></Field>
          </Row>
          <Row>
            <Field label={t('regon')}><Text field="regon_number" /></Field>
            <Field label={t('pkd')}>
              <Select field="pkd_number" options={o.pkd_numbers} />
            </Field>
          </Row>
          {[['PLN', 'pln'], ['EUR', 'eur'], ['GBP', 'gbp'], ['USD', 'usd']].map(([cur, k]) => (
            <Row key={k}>
              <Field label={`${t('bankAccount')} (${cur})`}><Text field={`bank_account_${k}`} /></Field>
              <Field label={`${t('swift')} (${cur})`}><Text field={`bank_swift_${k}`} /></Field>
            </Row>
          ))}
        </Section>
    ),
    certs: (
        <Section title={t('secCerts')} hint={t('certsHint')} testId="intake-section-certificates">
          <Row>
            <Field label={t('a1')}>
              <select className={inputCls} value={form.a1_certificate || ''} onChange={(e) => set('a1_certificate', e.target.value)} data-testid="intake-a1">
                {yesNo.map((y) => <option key={y.v} value={y.v}>{y.l}</option>)}
              </select>
            </Field>
            <Field label={t('a1Expiry')}><Text field="a1_expiry_date" type="date" /></Field>
          </Row>
          {CERT_GROUPS.filter((g) => (certTypes[g.key] || []).length > 0).map((g) => (
            <div key={g.key} className="rounded-lg border border-slate-200 p-3">
              <p className="text-sm font-medium text-slate-800 mb-2">{t(g.label)}</p>
              <div className="space-y-2">
                {(certTypes[g.key] || []).map((type) => (
                  <div key={type} className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="flex items-center gap-2 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        className="h-5 w-5"
                        checked={certChecked(g.field, type)}
                        onChange={(e) => toggleCert(g.field, type, e.target.checked)}
                        data-testid={`intake-cert-${g.key}-${type}`}
                      />
                      <span className="text-sm text-slate-700 truncate">{vl(type)}</span>
                    </label>
                    {certChecked(g.field, type) && (
                      <span className="relative block sm:w-44">
                        <input
                          type="date"
                          className={`${inputCls} peer ${dateTextHiddenCls}`}
                          value={certExpiry(g.field, type) || ''}
                          onChange={(e) => setCertExpiry(g.field, type, e.target.value)}
                        />
                        <DateOverlay value={certExpiry(g.field, type) || ''} />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>
    ),
    visas: (
        <Section title={t('secVisas')} testId="intake-section-visas">
          {[
            { key: 'eu_visa', typeKey: 'eu_visa_type', expKey: 'eu_visa_expiry_date', label: t('euVisa'), types: o.eu_visa_types },
            { key: 'us_visa', typeKey: 'us_visa_type', expKey: 'us_visa_expiry_date', label: t('usVisa'), types: o.us_visa_types },
            { key: 'uk_work_permit', typeKey: 'uk_work_permit_type', expKey: 'uk_work_permit_expiry_date', label: t('ukPermit'), types: o.uk_work_permit_types },
          ].map((v) => (
            <div key={v.key} className="rounded-lg border border-slate-200 p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Field label={v.label}>
                <select className={inputCls} value={form[v.key] || ''} onChange={(e) => set(v.key, e.target.value)} data-testid={`intake-${v.key}`}>
                  {yesNo.map((y) => <option key={y.v} value={y.v}>{y.l}</option>)}
                </select>
              </Field>
              <Field label={t('type')}>
                <Select field={v.typeKey} options={v.types} />
              </Field>
              <Field label={t('expiry')}><Text field={v.expKey} type="date" /></Field>
            </div>
          ))}
        </Section>
    ),
    documents: (
        <Section
          title={t('secFiles')}
          hint={t('filesHint', { n: meta.max_files || 6 })}
          testId="intake-section-files"
        >
          <label className="inline-flex flex-wrap items-center gap-3 cursor-pointer" data-testid="intake-file-picker">
            <span className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-within:ring-2 focus-within:ring-slate-400">
              {t('chooseFiles')}
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                onChange={pickFiles}
                className="sr-only"
                data-testid="intake-file-input"
              />
            </span>
            <span className="text-sm text-slate-500" data-testid="intake-file-status">
              {files.length ? t('filesChosen', { n: files.length }) : t('noFilesChosen')}
            </span>
          </label>
          {files.length > 0 && (
            <ul className="space-y-2" data-testid="intake-file-list">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-2"
                >
                  <span className="truncate flex-1 min-w-0">{f.name}</span>
                  {/* Same document types as employee attachments in the app. */}
                  <select
                    className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white w-full sm:w-52"
                    value={fileTypes[i] || ''}
                    onChange={(e) => setFileTypes((prev) => {
                      const next = [...prev];
                      next[i] = e.target.value;
                      return next;
                    })}
                    aria-label={t('docType')}
                    data-testid={`intake-file-type-${i}`}
                  >
                    <option value="">{t('docTypePick')}</option>
                    {(o.document_types || []).map((d) => (
                      <option key={d} value={d}>{vl(d)}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="text-xs text-red-600 shrink-0 self-start sm:self-auto"
                    onClick={() => {
                      setFiles((prev) => prev.filter((_, j) => j !== i));
                      setFileTypes((prev) => prev.filter((_, j) => j !== i));
                    }}
                    data-testid={`intake-file-remove-${i}`}
                  >
                    {t('remove')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>
    ),
    notes: (
        <Section title={t('secNotes')} testId="intake-section-notes">
          <textarea
            rows={3}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base sm:text-sm"
            placeholder={t('notesPlaceholder')}
            value={form.candidate_notes || ''}
            onChange={(e) => set('candidate_notes', e.target.value)}
            data-testid="intake-notes"
          />
        </Section>
    ),
  };

  return (
    <IntakeCtx.Provider value={ctx}>
    <div className="min-h-screen bg-slate-50 py-6 sm:py-10">
      <LinkContext.Provider value={linkCtx}>
      <form
        onSubmit={submit}
        className="max-w-3xl mx-auto bg-white rounded-lg shadow p-4 sm:p-8"
        data-testid="candidate-intake-form"
      >
        {/* Header: title left, logo + language toggle right */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-800 break-words">
              {companyName ? `${companyName} — ${t('headingSuffix')}` : t('headingSuffix')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{meta.label}</p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={companyName || ''}
                className="h-9 sm:h-12 w-auto max-w-[140px] sm:max-w-[200px] object-contain"
                data-testid="intake-logo"
              />
            )}
            <LangToggle />
          </div>
        </div>
        {meta.note && <p className="text-sm text-slate-700 mt-3 p-3 bg-blue-50 border border-blue-100 rounded">{meta.note}</p>}

        {restored && (
          <div
            className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded bg-emerald-50 border border-emerald-200"
            data-testid="intake-draft-restored"
          >
            <p className="text-sm text-emerald-900">{t('draftFound')}</p>
            <button
              type="button"
              onClick={clearDraft}
              className="text-xs font-medium text-emerald-800 underline self-start sm:self-auto"
              data-testid="intake-draft-clear"
            >
              {t('draftClear')}
            </button>
          </div>
        )}

        <Section title={t('secContact')} hint={t('contactHint')} testId="intake-section-contact">
          <Row>
            <Field label={t('firstName')} required><Text field="first_name" testId="intake-first-name" required /></Field>
            <Field label={t('lastName')} required><Text field="last_name" testId="intake-last-name" required /></Field>
          </Row>
          <Row>
            <Field label={t('middleName')}><Text field="middle_name" /></Field>
          </Row>
          <Row>
            <Field label={t('email')} required><Text field="email" type="email" testId="intake-email" required /></Field>
            <Field label={t('phone')} required><Text field="phone" testId="intake-phone" required /></Field>
          </Row>
          <Row>
            <Field label={t('education')}>
              <Select field="education" options={o.education_levels} testId="intake-education" />
            </Field>
            <Field label={`${t('occupation')} — ${t('occupationHint')}`}>
              <>
                <input
                  className={inputCls}
                  list="intake-occupations"
                  value={form.occupation || ''}
                  onChange={(e) => set('occupation', e.target.value)}
                  data-testid="intake-occupation"
                />
                <datalist id="intake-occupations">
                  {(o.occupations || []).map((x) => <option key={x} value={x} label={vl(x)} />)}
                </datalist>
              </>
            </Field>
          </Row>
          <Row>
            <Field label={t('country')}>
              <Select field="country" options={o.countries} testId="intake-country" />
            </Field>
          </Row>
          <Row full>
            <Field label={t('address')}><Text field="address" /></Field>
          </Row>
          <Row full>
            <Field label={t('website')}><Text field="url" /></Field>
          </Row>
        </Section>

        {(meta.blocks || []).map((b) => (b.type === 'builtin'
          ? <React.Fragment key={b.id}>{builtinSections[b.key] || null}</React.Fragment>
          : <IntakeCustomBlock key={b.id} block={b} lang={lang}
              value={(form.custom_answers || {})[b.id]}
              onChange={(v) => setForm((f) => ({ ...f, custom_answers: { ...(f.custom_answers || {}), [b.id]: v } }))} />))}

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
          {progress && <span className="text-xs text-slate-500">{progress}</span>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto h-12 sm:h-11 px-6 rounded-md bg-slate-900 text-white font-medium disabled:opacity-60"
            data-testid="intake-submit"
          >
            {submitting ? t('sending') : t('submit')}
          </button>
        </div>
      </form>
      </LinkContext.Provider>
    </div>
    </IntakeCtx.Provider>
  );
}
